import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Bar } from 'react-chartjs-2';

const RED = '#f45e5a';
const BLUE = '#6c87fc';
const NAVY = '#1f2f7a';
const FONT = 'Lato,"Helvetica Neue",Helvetica,Arial,sans-serif';

export const minus = (s) => String(s).replace('-', '\u2212');

/**
 * Turns the histogram object ({ '<-30': 0.05, '-30 : -26': 0.4, ... '>30': 24.8 })
 * into a sorted list of buckets. `end` is the exclusive upper edge.
 */
export const parseBuckets = (hist) =>
  Object.entries(hist || {})
    .map(([key, value]) => {
      const k = key.trim();
      // pull the numbers out of the key, whatever the exact format
      // ('<-30', '< -30', '>30', '>=30', '30+', '-30 : -26', '0 to 4', ...)
      const nums = (k.match(/-?\d+(\.\d+)?/g) || []).map(Number);
      let kind = 'range';
      let start;
      let end;

      if (k.startsWith('<')) {
        kind = 'below';
        end = nums[0];
      } else if (nums.length < 2 || k.startsWith('>') || /inf/i.test(k)) {
        // open-ended top bucket, e.g. '>30' or '30+'
        kind = 'above';
        start = nums[0];
      } else {
        [start] = nums;
        end = nums[1] + 1;
      }

      let sortKey = start;
      if (kind === 'below') sortKey = -Infinity;
      if (kind === 'above') sortKey = Infinity;

      return {
        key,
        kind,
        start,
        end,
        value: Number(value),
        sortKey,
        isRuin: kind === 'below' || (kind === 'range' && start < 0),
      };
    })
    .sort((a, b) => a.sortKey - b.sortKey);

export const bucketLabel = (b) => {
  if (b.kind === 'below') return `Less than ${minus(b.end)}`;
  if (b.kind === 'above') return `${b.start} or more`;
  return `${minus(b.start)} to ${minus(b.end - 1)}`;
};

const formatBarValue = (v) => {
  if (v > 0 && v < 0.1) return '<0.1%';
  return `${v.toFixed(1)}%`;
};

const roundedRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
};

// Draws bar values, edge tick labels, the zero line, direction hints and the median marker.
// Reads its data from options.plugins.ruinAnnotations so it never goes stale.
const annotationPlugin = {
  id: 'ruinAnnotations',
  afterDatasetsDraw(chart, easing, opts) {
    const { buckets, median } = opts || {};
    if (!buckets || buckets.length < 2) return;

    const { ctx, chartArea } = chart;
    const xScale = chart.scales['x-axis-0'];
    const yScale = chart.scales['y-axis-0'];
    const n = buckets.length;
    const w = Math.abs(xScale.getPixelForTick(1) - xScale.getPixelForTick(0));
    const center = (i) => xScale.getPixelForTick(i);
    const left = (i) => center(i) - w / 2;

    ctx.save();
    ctx.font = `14px ${FONT}`;

    // bar values
    ctx.fillStyle = '#4b5563';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    buckets.forEach((b, i) => {
      ctx.fillText(
        formatBarValue(b.value),
        center(i),
        yScale.getPixelForValue(b.value) - 5
      );
    });

    // edge tick marks + labels
    ctx.textBaseline = 'top';
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    for (let i = 1; i < n; i += 1) {
      const x = left(i);
      ctx.beginPath();
      ctx.moveTo(x, chartArea.bottom);
      ctx.lineTo(x, chartArea.bottom + 4);
      ctx.stroke();
      ctx.fillStyle = '#6b7280';
      ctx.fillText(minus(buckets[i].start), x, chartArea.bottom + 8);
    }

    // zero line + direction hints
    const zeroIdx = buckets.findIndex((b) => !b.isRuin);
    if (zeroIdx > 0) {
      const x0 = left(zeroIdx);
      ctx.save();
      ctx.setLineDash([2, 3]);
      ctx.strokeStyle = '#6b7280';
      ctx.beginPath();
      ctx.moveTo(x0, chartArea.top);
      ctx.lineTo(x0, chartArea.bottom);
      ctx.stroke();
      ctx.restore();

      const leftLabel = 'Money runs out first';
      const rightLabel = 'Money outlives you';
      const arrowGap = 4;
      ctx.font = `bold 14px ${FONT}`;
      const leftLabelWidth = ctx.measureText(leftLabel).width;
      const rightLabelWidth = ctx.measureText(rightLabel).width;
      ctx.font = `bold 20px ${FONT}`;
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#d32f2f';
      ctx.textAlign = 'right';
      ctx.font = `bold 14px ${FONT}`;
      ctx.fillText(leftLabel, x0 - 8, chartArea.bottom + 34);
      ctx.font = `bold 20px ${FONT}`;
      ctx.fillText(
        '\u2190',
        x0 - 8 - leftLabelWidth - arrowGap,
        chartArea.bottom + 32
      );
      ctx.fillStyle = NAVY;
      ctx.textAlign = 'left';
      ctx.font = `bold 14px ${FONT}`;
      ctx.fillText(rightLabel, x0 + 8, chartArea.bottom + 34);
      ctx.font = `bold 20px ${FONT}`;
      ctx.fillText(
        '\u2192',
        x0 + 8 + rightLabelWidth + arrowGap,
        chartArea.bottom + 32
      );
    }

    // median marker
    if (
      median !== null &&
      median !== undefined &&
      !Number.isNaN(Number(median))
    ) {
      const m = Number(median);
      let x = null;
      for (let i = 0; i < n; i += 1) {
        const b = buckets[i];
        if (b.kind === 'range' && m >= b.start && m < b.end) {
          x = left(i) + ((m - b.start) / (b.end - b.start)) * w;
          break;
        }
      }
      if (x === null) {
        const idx = m < 0 ? 0 : n - 1;
        x = center(idx);
      }

      const text = `Ruin median: ${minus(m)} years`;
      ctx.font = `bold 14px ${FONT}`;
      const padX = 10;
      const boxW = ctx.measureText(text).width + padX * 2;
      const boxH = 22;
      const boxX = Math.max(
        chartArea.left,
        Math.min(x - boxW / 2, chartArea.right - boxW)
      );
      const boxY = chartArea.top - boxH - 6;

      ctx.strokeStyle = NAVY;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, boxY + boxH);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();

      ctx.fillStyle = NAVY;
      roundedRect(ctx, boxX, boxY, boxW, boxH, 5);
      ctx.fill();

      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, boxX + boxW / 2, boxY + boxH / 2 + 0.5);
    }

    ctx.restore();
  },
};

const plugins = [annotationPlugin];

const DistributionChart = ({ data: dist }) => {
  const buckets = parseBuckets(dist.hist);

  const data = {
    labels: buckets.map(bucketLabel),
    datasets: [
      {
        label: 'Probability (%)',
        data: buckets.map((b) => b.value),
        backgroundColor: buckets.map((b) => (b.isRuin ? RED : BLUE)),
        categoryPercentage: 1,
        barPercentage: 0.95,
      },
    ],
  };

  const options = {
    maintainAspectRatio: true,
    legend: { display: false },
    hover: { mode: 'index', intersect: false },
    tooltips: {
      mode: 'index',
      intersect: false,
      titleFontSize: 14,
      bodyFontSize: 14,
      footerFontSize: 14,
      callbacks: {
        title: (items) => {
          const b = buckets[items[0].index];
          return b ? `${bucketLabel(b)} years` : '';
        },
        label: (item) => {
          const b = buckets[item.index];
          const kind = b && b.isRuin ? 'Ruin' : 'Safe';
          return `${kind} scenario: ${Number(item.yLabel).toFixed(2)}%`;
        },
      },
    },
    layout: { padding: { top: 40, bottom: 52, left: 4, right: 8 } },
    scales: {
      xAxes: [
        {
          id: 'x-axis-0',
          type: 'category',
          gridLines: { display: false, drawBorder: false },
          ticks: { display: false },
        },
      ],
      yAxes: [
        {
          id: 'y-axis-0',
          position: 'left',
          gridLines: {
            color: '#eeeeee',
            zeroLineColor: '#e5e7eb',
            drawBorder: false,
          },
          ticks: {
            beginAtZero: true,
            maxTicksLimit: 6,
            padding: 8,
            fontSize: 14,
            fontColor: '#6b7280',
            callback: (value) => `${value}%`,
          },
        },
      ],
    },
    plugins: {
      ruinAnnotations: { buckets, median: dist.median },
    },
  };

  return (
    <Wrapper>
      <Title>
        The Distribution of How Likely You Will Outlive Your Money by How Many
        Years
      </Title>
      <Legend>
        <LegendItem color={BLUE}>Safe scenarios</LegendItem>
        <LegendItem color={RED}>Ruin scenarios</LegendItem>
      </Legend>
      <Bar
        aria-label="Probability of Ruin Distribution Chart"
        role="img"
        data={data}
        options={options}
        plugins={plugins}
        width={760}
        height={400}
      />
      <Caption>
        Years you outlive your money (&minus;) or your money outlives you (+)
      </Caption>
    </Wrapper>
  );
};

DistributionChart.propTypes = {
  data: PropTypes.object.isRequired,
};

export default DistributionChart;

const Wrapper = styled.div`
  width: 100%;
  text-align: center;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #222;
  margin-bottom: 0.6rem;
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.25rem;
  margin-bottom: 0.25rem;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  color: #333;

  &:before {
    content: '';
    display: inline-block;
    width: 0.7rem;
    height: 0.7rem;
    margin-right: 0.4rem;
    border-radius: 2px;
    background: ${(p) => p.color};
  }
`;

const Caption = styled.div`
  margin-top: -1rem;
  font-size: 14px;
  color: #6b7280;
`;
